import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Icon from '../AppIcon';

const API_URL = 'https://api.autocasses.com/api/categories/all';

const CategoryGrid = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loadingImages, setLoadingImages] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);
  
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [touchStart, setTouchStart] = useState(0);

  useEffect(() => {
    fetchCategories();
  }, [i18n.language]);

  useEffect(() => {
    updateScrollButtons();
    const handleResize = () => updateScrollButtons();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [categories]);

  const updateScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      const container = scrollContainerRef.current;
      
      if (direction === 'left') {
        container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else {
        container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
      
      setTimeout(updateScrollButtons, 300);
    }
  };

  // Gestion du drag pour le défilement horizontal
  const handleMouseDown = (e) => {
    if (scrollContainerRef.current) {
      setIsDragging(true);
      setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
      setScrollLeft(scrollContainerRef.current.scrollLeft);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // Multiplier pour un défilement plus rapide
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    updateScrollButtons();
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!scrollContainerRef.current) return;
    e.preventDefault();
    const touchX = e.touches[0].clientX;
    const diff = touchStart - touchX;
    scrollContainerRef.current.scrollLeft += diff * 1.5;
    setTouchStart(touchX);
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(API_URL, {
        timeout: 15000,
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      
      const data = response.data;
      
      if (Array.isArray(data)) {
        const transformedCategories = data.map((category, index) => {
          const themeColors = {
            'moto': ['#ef4444', '#b91c1c', '#dc2626'],
            'moteur': ['#3b82f6', '#1d4ed8', '#2563eb'],
            'art': ['#8b5cf6', '#7c3aed', '#9333ea'],
            'voiture': ['#f59e0b', '#d97706', '#fbbf24'],
            'carrosserie': ['#10b981', '#059669', '#34d399'],
            'frein': ['#6366f1', '#4f46e5', '#818cf8'],
            'suspension': ['#06b6d4', '#0891b2', '#22d3ee'],
            'transmission': ['#f97316', '#ea580c', '#fb923c'],
            'échappement': ['#64748b', '#475569', '#94a3b8'],
            'default': ['#8b5cf6', '#7c3aed', '#a78bfa']
          };
          
          const nomLower = category.nom.toLowerCase();
          let themeKey = 'default';
          
          if (nomLower.includes('moto')) themeKey = 'moto';
          else if (nomLower.includes('moteur')) themeKey = 'moteur';
          else if (nomLower.includes('art')) themeKey = 'art';
          else if (nomLower.includes('voiture') || nomLower.includes('auto')) themeKey = 'voiture';
          else if (nomLower.includes('carrosserie')) themeKey = 'carrosserie';
          else if (nomLower.includes('frein')) themeKey = 'frein';
          else if (nomLower.includes('suspension')) themeKey = 'suspension';
          else if (nomLower.includes('transmission')) themeKey = 'transmission';
          else if (nomLower.includes('échappement')) themeKey = 'échappement';
          
          const colors = themeColors[themeKey];
          
          const getIcon = (nom) => {
            const nomLower = nom.toLowerCase();
            
            if (nomLower.includes('moto')) {
              return { icon: 'Bike', size: 24 };
            } else if (nomLower.includes('moteur')) {
              return { icon: 'Cog', size: 26 };
            } else if (nomLower.includes('art')) {
              return { icon: 'Palette', size: 24 };
            } else if (nomLower.includes('voiture') || nomLower.includes('auto')) {
              return { icon: 'Car', size: 26 };
            } else if (nomLower.includes('carrosserie')) {
              return { icon: 'Wrench', size: 24 };
            } else if (nomLower.includes('frein')) {
              return { icon: 'Brakes', size: 24 };
            } else if (nomLower.includes('suspension')) {
              return { icon: 'Shield', size: 24 };
            } else if (nomLower.includes('transmission')) {
              return { icon: 'Settings', size: 26 };
            } else if (nomLower.includes('échappement')) {
              return { icon: 'Flame', size: 24 };
            } else {
              return { icon: 'Package', size: 26 };
            }
          };
          
          const { icon, size } = getIcon(category.nom);
          
          const placeholderImages = {
            'moto': 'https://images.unsplash.com/photo-1558981806-ec527fa84c39?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
            'moteur': 'https://images.unsplash.com/photo-1530497610245-94d3c16cda28?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
            'art': 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
            'voiture': 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
            'carrosserie': 'https://images.unsplash.com/photo-1553440569-bcc63803a83d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
            'default': 'https://images.unsplash.com/photo-1549399542-7e3f8b79c341?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80'
          };
          
          const imageUrl = category.photo_url || placeholderImages[themeKey] || placeholderImages.default;
          
          const getDescription = () => {
            if (category.description?.trim()) {
              return category.description;
            }
            
            const descriptions = {
              'moto': t('Pièces et accessoires moto'),
              'moteur': t('Moteurs et composants'),
              'art': t('Œuvres d\'art uniques'),
              'voiture': t('Pièces automobile complètes'),
              'carrosserie': t('Carrosserie et peinture'),
              'frein': t('Systèmes de freinage'),
              'suspension': t('Suspensions et amortisseurs'),
              'transmission': t('Transmissions et boîtes'),
              'échappement': t('Systèmes d\'échappement'),
              'default': t('Produits de qualité')
            };
            
            return descriptions[themeKey] || descriptions.default;
          };
          
          const itemCount = Math.floor(Math.random() * 2000) + 100;
          
          return {
            id: `cat-${category.id}`,
            originalId: category.id,
            title: category.nom,
            description: getDescription(),
            image: imageUrl,
            icon,
            iconSize: size,
            itemCount: itemCount.toLocaleString(),
            featured: index < 3,
            colors,
            theme: themeKey,
            createdAt: category.createdAt || new Date().toISOString()
          };
        });
        
        transformedCategories.sort((a, b) => {
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        
        setCategories(transformedCategories);
      } else {
        throw new Error(t('Format de réponse API invalide'));
      }
    } catch (error) {
      console.error(t('Error fetching categories:'), error);
      setError(t('Impossible de charger les catégories. Vérifiez votre connexion.'));
      setCategories(getDemoCategories());
    } finally {
      setLoading(false);
    }
  };

  const getDemoCategories = () => {
    const currentLang = i18n.language;
    return [
      {
        id: '1',
        originalId: 1,
        title: currentLang === 'fr' ? t('Pièces Moto') : t('Motorcycle Parts'),
        description: currentLang === 'fr' ? t('Accessoires et pièces détachées') : t('Accessories and spare parts'),
        image: 'https://images.unsplash.com/photo-1558981806-ec527fa84c39?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
        icon: 'Bike',
        iconSize: 26,
        itemCount: '1,850',
        featured: true,
        colors: ['#ef4444', '#b91c1c', '#dc2626'],
        theme: 'moto'
      },
      {
        id: '2',
        originalId: 2,
        title: currentLang === 'fr' ? t('Moteurs') : t('Engines'),
        description: currentLang === 'fr' ? t('Moteurs complets et pièces') : t('Complete engines and parts'),
        image: 'https://images.unsplash.com/photo-1530497610245-94d3c16cda28?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
        icon: 'Cog',
        iconSize: 28,
        itemCount: '950',
        featured: true,
        colors: ['#3b82f6', '#1d4ed8', '#2563eb'],
        theme: 'moteur'
      },
      {
        id: '3',
        originalId: 3,
        title: currentLang === 'fr' ? t('Carrosserie') : t('Bodywork'),
        description: currentLang === 'fr' ? t('Pièces de carrosserie auto') : t('Auto body parts'),
        image: 'https://images.unsplash.com/photo-1553440569-bcc63803a83d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
        icon: 'Wrench',
        iconSize: 26,
        itemCount: '2,340',
        featured: true,
        colors: ['#10b981', '#059669', '#34d399'],
        theme: 'carrosserie'
      },
      {
        id: '4',
        originalId: 4,
        title: currentLang === 'fr' ? t('Freins') : t('Brakes'),
        description: currentLang === 'fr' ? t('Systèmes de freinage') : t('Braking systems'),
        image: 'https://images.unsplash.com/photo-1614162692292-7ac56d7f7f1e?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
        icon: 'Brakes',
        iconSize: 26,
        itemCount: '1,250',
        featured: false,
        colors: ['#6366f1', '#4f46e5', '#818cf8'],
        theme: 'frein'
      },
      {
        id: '5',
        originalId: 5,
        title: currentLang === 'fr' ? t('Suspensions') : t('Suspensions'),
        description: currentLang === 'fr' ? t('Amortisseurs et ressorts') : t('Shock absorbers and springs'),
        image: 'https://images.unsplash.com/photo-1580274455191-1c62238fa333?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
        icon: 'Shield',
        iconSize: 26,
        itemCount: '780',
        featured: false,
        colors: ['#06b6d4', '#0891b2', '#22d3ee'],
        theme: 'suspension'
      },
      {
        id: '6',
        originalId: 6,
        title: currentLang === 'fr' ? t('Transmissions') : t('Transmissions'),
        description: currentLang === 'fr' ? t('Boîtes de vitesses et embrayages') : t('Gearboxes and clutches'),
        image: 'https://images.unsplash.com/photo-1593941707882-a5bba5338fe2?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
        icon: 'Settings',
        iconSize: 28,
        itemCount: '920',
        featured: false,
        colors: ['#f97316', '#ea580c', '#fb923c'],
        theme: 'transmission'
      },
      {
        id: '7',
        originalId: 7,
        title: currentLang === 'fr' ? t('Échappements') : t('Exhausts'),
        description: currentLang === 'fr' ? t('Lignes d\'échappement complètes') : t('Complete exhaust lines'),
        image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
        icon: 'Flame',
        iconSize: 26,
        itemCount: '640',
        featured: false,
        colors: ['#64748b', '#475569', '#94a3b8'],
        theme: 'échappement'
      },
      {
        id: '8',
        originalId: 8,
        title: currentLang === 'fr' ? t('Accessoires') : t('Accessories'),
        description: currentLang === 'fr' ? t('Accessoires divers automobile') : t('Various auto accessories'),
        image: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
        icon: 'Package',
        iconSize: 28,
        itemCount: '3,150',
        featured: false,
        colors: ['#8b5cf6', '#7c3aed', '#a78bfa'],
        theme: 'default'
      }
    ];
  };

  const handleCategoryPress = (category, index) => {
    setActiveIndex(index);
    
    setTimeout(() => {
      navigate('/rechercher', { 
        state: { 
          category: category.originalId,
          categoryName: category.title
        }
      });
    }, 150);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64 bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <p className="mt-4 text-slate-600 font-medium">
          {t('Chargement des catégories...')}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-slate-50 to-white px-4 md:px-8 py-8 md:py-12">
      {/* Header Section */}
      <div className="max-w-7xl mx-auto mb-8 md:mb-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
              {t('Catégories')}
            </h1>
            <p className="text-slate-600 mt-2">
              {categories.length} {t('catégories disponibles')}
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-4">
              <button
                onClick={() => handleScroll('left')}
                disabled={!canScrollLeft}
                className={`p-2 rounded-full shadow-md transition-all duration-200 ${
                  canScrollLeft 
                    ? 'bg-white hover:bg-slate-50 cursor-pointer' 
                    : 'bg-slate-100 cursor-not-allowed'
                }`}
              >
                <Icon 
                  name="ChevronLeft" 
                  size={24} 
                  color={canScrollLeft ? "#3b82f6" : "#94a3b8"} 
                />
              </button>
              
              <button
                onClick={() => handleScroll('right')}
                disabled={!canScrollRight}
                className={`p-2 rounded-full shadow-md transition-all duration-200 ${
                  canScrollRight 
                    ? 'bg-white hover:bg-slate-50 cursor-pointer' 
                    : 'bg-slate-100 cursor-not-allowed'
                }`}
              >
                <Icon 
                  name="ChevronRight" 
                  size={24} 
                  color={canScrollRight ? "#3b82f6" : "#94a3b8"} 
                />
              </button>
            </div>
            
            <button 
              onClick={fetchCategories}
              className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
            >
              <Icon name="RefreshCw" size={18} color="#64748b" />
              <span className="text-slate-700 font-medium text-sm">
                {t('Actualiser')}
              </span>
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center">
            <Icon name="AlertCircle" size={20} color="#ef4444" className="flex-shrink-0" />
            <p className="ml-3 text-red-700 text-sm flex-1">{error}</p>
            <button 
              onClick={fetchCategories}
              className="ml-3 text-red-600 font-semibold text-sm hover:text-red-800 flex items-center"
            >
              <Icon name="RefreshCw" size={16} className="mr-1" />
              {t('Réessayer')}
            </button>
          </div>
        )}
      </div>

      {/* Horizontal Scroll Container */}
      <div className="max-w-7xl mx-auto relative">
        <div className="hidden md:block absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-slate-50 to-transparent pointer-events-none z-10"></div>
        <div className="hidden md:block absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-slate-50 to-transparent pointer-events-none z-10"></div>
        
        {/* Scroll Container */}
        <div 
          ref={scrollContainerRef}
          className="flex overflow-x-auto pb-8 -mx-4 px-4 md:px-0 scrollbar-hide"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onScroll={updateScrollButtons}
          style={{ 
            cursor: isDragging ? 'grabbing' : 'grab',
            scrollBehavior: 'smooth',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
        >
          {categories.map((category, index) => (
            <div
              key={category.id}
              className="flex-shrink-0 w-80 md:w-96 mx-2 transition-all duration-300"
            >
              <div 
                className={`bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 ${
                  activeIndex === index ? 'scale-95 ring-2 ring-blue-200' : ''
                }`}
                style={{
                  '--card-color': category.colors[0],
                  height: '420px'
                }}
              >
                {/* Featured Ribbon */}
                {category.featured && (
                  <div 
                    className="absolute top-4 left-0 flex items-center px-4 py-2 rounded-r-lg z-10 shadow-lg"
                    style={{ backgroundColor: category.colors[0] }}
                  >
                    <Icon name="Star" size={14} color="white" />
                    <span className="ml-2 text-white text-xs font-bold tracking-wide uppercase">
                      {t('Populaire')}
                    </span>
                  </div>
                )}

                {/* Image Container */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.title}
                    className={`w-full h-full object-cover transition-transform duration-500 hover:scale-110 ${
                      loadingImages[category.id] ? 'opacity-50' : ''
                    }`}
                    onLoadStart={() => setLoadingImages(prev => ({...prev, [category.id]: true}))}
                    onLoad={() => setLoadingImages(prev => ({...prev, [category.id]: false}))}
                    onError={(e) => {
                      setLoadingImages(prev => ({...prev, [category.id]: false}));
                      const fallbackImages = {
                        'moto': 'https://images.unsplash.com/photo-1558981806-ec527fa84c39?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
                        'moteur': 'https://images.unsplash.com/photo-1530497610245-94d3c16cda28?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
                        'default': 'https://images.unsplash.com/photo-1549399542-7e3f8b79c341?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80'
                      };
                      e.target.src = fallbackImages[category.theme] || fallbackImages.default;
                    }}
                  />
                  
                  {/* Loading Overlay */}
                  {loadingImages[category.id] && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/50">
                      <div 
                        className="animate-spin rounded-full h-10 w-10 border-b-2"
                        style={{ borderColor: category.colors[0] }}
                      ></div>
                    </div>
                  )}

                  {/* Gradient Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/30 to-transparent"></div>

                  {/* Icon Badge */}
                  <div 
                    className="absolute top-4 right-4 p-2 rounded-xl backdrop-blur-md bg-white/20 shadow-lg"
                    style={{ backgroundColor: `${category.colors[0]}30` }}
                  >
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
                      style={{ backgroundColor: category.colors[0] }}
                    >
                      <Icon 
                        name={category.icon} 
                        size={category.iconSize} 
                        color="white"
                      />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-slate-900 pr-4 truncate"> 
                      {category.title}
                    </h3>
                    <Icon 
                      name="ArrowRight" 
                      size={24} 
                      color={category.colors[1]}
                      className="flex-shrink-0 opacity-80 mt-1"
                    />
                  </div>
                  
                  <p className="text-slate-600 text-base mb-6 line-clamp-2 min-h-[3rem] truncate">
                    {category.description}
                  </p>

                  <button
                    onClick={() => handleCategoryPress(category, index)}
                    className="w-full py-4 rounded-xl font-bold text-base transition-all duration-300 hover:shadow-xl flex items-center justify-center gap-3 group"
                    style={{
                      background: `linear-gradient(135deg, ${category.colors[0]}, ${category.colors[1]})`,
                      color: 'white'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <Icon name="Search" size={20} color="white" />
                    <span>{t('Explorer cette catégorie')}</span>
                    <Icon 
                      name="ArrowRight" 
                      size={20} 
                      color="white"
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Indicators for Mobile */}
      <div className="md:hidden flex justify-center items-center gap-2 mt-6">
        <div className="flex gap-2">
          {categories.slice(0, 3).map((_, index) => (
            <div 
              key={index}
              className={`w-2 h-2 rounded-full ${
                index === 0 ? 'bg-blue-500' : 'bg-slate-300'
              }`}
            />
          ))}
        </div>
        <span className="text-slate-500 text-sm ml-2">
          {t('Faites glisser pour plus')}
        </span>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        /* Smooth scrolling and better performance */
        * {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        
        /* Improve touch experience */
        @media (hover: none) and (pointer: coarse) {
          button, 
          [role="button"] {
            min-height: 44px;
            min-width: 44px;
          }
        }
        
        /* Animation for cards */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .card-animation {
          animation: fadeInUp 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default CategoryGrid;